import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { GetUser as User } from "@redotech/redo-model/user";
import {
  RedoAvatar,
  type AvatarContent,
} from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

interface EditableAssigneeCellProps {
  supportTicket: SupportTicketListItemResponse;
}

function isAiUser(user: User): boolean {
  return user._id === CHATBOT_USER_ID || user._id === AI_AUTOPILOT_USER_ID;
}

function getAvatarContent(user: User): AvatarContent {
  if (isAiUser(user)) {
    return { type: "icon", Icon: AiStarIcon };
  }
  const imageUrl = getUserAvatarUrl({ email: user.email, userId: user._id });
  return {
    type: "image",
    imageUrl,
    altText: user.name,
    backup: { type: "text", text: user.name },
  };
}

export const EditableAssigneeCell = memo(function EditableAssigneeCell({
  supportTicket,
}: EditableAssigneeCellProps) {
  const team = useRequiredContext(TeamContext);

  const assignee = useMemo(() => {
    const { mongoAssigneeId } = supportTicket;
    if (!mongoAssigneeId) {
      return undefined;
    }
    const hexId = MongoUserId.toObjectId(mongoAssigneeId).toHexString();
    const teamUser = team.users.find(
      (u) => typeof u.user === "object" && u.user?._id === hexId,
    );
    //@Connor fix this
    return teamUser && typeof teamUser.user === "object"
      ? (teamUser.user as User)
      : undefined;
  }, [supportTicket, team.users]);

  const { assigneeType } = supportTicket;

  if (assigneeType === "AI" || (assignee && isAiUser(assignee))) {
    return (
      <Flex align="center">
        <RedoIcon color="brand" Icon={AiStarIcon} size={20} />
      </Flex>
    );
  }

  if (assigneeType === "CHAT_FLOW") {
    return (
      <Flex align="center">
        <RedoIcon color="blue" Icon={MessageChatCircleIcon} size={20} />
      </Flex>
    );
  }

  if (!assignee) {
    return null;
  }

  return (
    <Flex align="center">
      <RedoAvatar content={getAvatarContent(assignee)} size="xs" />
    </Flex>
  );
});
